<template>
  <div class="ldt-panel" v-if="showLidarPanel && lidarPanelVisible">

    <!-- Header -->
    <div class="ldt-header">
      <div class="ldt-header-left">
        <fa :icon="['fas', 'layer-group']" class="ldt-header-ico" />
        <span class="ldt-header-title">Lidar Groups</span>
      </div>
      <fa
        id="minOrMaxLidarPanelElement"
        class="ldt-header-toggle"
        :title="lidarMinimized ? 'Expand' : 'Collapse'"
        :icon="['fas', lidarMinimized ? 'chevron-down' : 'chevron-up']"
        @click="toggleLidarHeight()"
      />
    </div>

    <!-- Body -->
    <div class="ldt-body scrollable" v-if="!lidarMinimized">

      <!-- Group list -->
      <template v-if="!lidarIsLoading && distinct_lidar.length > 0">
        <div
          v-for="IDobj in distinct_lidar"
          :key="IDobj.dateCode"
          class="ldt-group"
        >
          <!-- Group row -->
          <div
            class="ldt-group-row"
            :class="{ 'ldt-group-row--active': anyPointcloudOfLidarGroupVisible(IDobj.dateCode) === 'btn-primary' }"
            @click="toggleLidarGroup(IDobj, true, $event)"
          >
            <span class="ldt-group-name">{{ IDobj.dateCode }}</span>
            <fa
              class="ldt-group-caret"
              :class="{ 'ldt-group-caret--open': !IDobj.groupIsCollapsed }"
              :icon="['fas', 'chevron-right']"
              @click.stop="expandLidarGroup(IDobj)"
            />
          </div>

          <!-- Point cloud chips -->
          <transition name="ldt-slide">
            <div v-if="!IDobj.groupIsCollapsed" class="ldt-chips">
              <button
                v-for="pointcloudID in IDobj.pointcloudIDArr"
                :key="pointcloudID"
                class="ldt-chip"
                :class="{ 'ldt-chip--on': availableLidarButtonCtrl(pointcloudID) === 'btn-primary' }"
                @click="toggleLidarLayer(pointcloudID, IDobj.dateCode)"
              >
                {{ pointcloudID }}
              </button>
            </div>
          </transition>
        </div>
      </template>

      <!-- Zoom warning -->
      <div class="ldt-state" v-else-if="!lidarMinZoomAchieved">
        <fa :icon="['fas', 'search-location']" class="ldt-state-ico ldt-state-ico--blue" />
        <p class="ldt-state-msg">Zoom to level <strong>17+</strong> to load lidar</p>
      </div>

      <!-- Loading -->
      <div class="ldt-state" v-else-if="lidarIsLoading">
        <fa :icon="['fas', 'spinner']" class="ldt-state-ico fa-spin" />
        <p class="ldt-state-msg">Loading lidar...</p>
      </div>

      <!-- No data -->
      <div class="ldt-state" v-else>
        <fa :icon="['fas', 'map-marked-alt']" class="ldt-state-ico ldt-state-ico--muted" />
        <p class="ldt-state-msg">No lidar at this location</p>
      </div>

    </div>
  </div>
</template>

<style>
/* Panel */
.ldt-panel {
  width: 13.75rem;
  max-height: 78vh;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #dde3ec;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: system-ui, -apple-system, sans-serif;
}

/* Header */
.ldt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5625rem 0.75rem;
  background: #063c6d;
  flex-shrink: 0;
}
.ldt-header-left {
  display: flex;
  align-items: center;
  gap: 0.4375rem;
}
.ldt-header-ico {
  color: rgba(255,255,255,0.7);
  font-size: 0.75rem;
}
.ldt-header-title {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #fff;
}
.ldt-header-toggle {
  color: rgba(255,255,255,0.6);
  font-size: 0.65rem;
  cursor: pointer;
  transition: color 0.15s;
}
.ldt-header-toggle:hover { color: #fff; }

/* Body */
.ldt-body {
  flex: 1;
  overflow-y: auto;
  padding: 0.375rem 0 0.25rem;
}

/* Scrollbar */
.ldt-panel .scrollable { scrollbar-width: none; }
.ldt-panel .scrollable:hover { scrollbar-width: thin; scrollbar-color: #c5cfd8 transparent; }
.ldt-panel .scrollable::-webkit-scrollbar { width: 0; }
.ldt-panel .scrollable:hover::-webkit-scrollbar { width: 4px; }
.ldt-panel .scrollable:hover::-webkit-scrollbar-track { background: transparent; }
.ldt-panel .scrollable:hover::-webkit-scrollbar-thumb { background: #c5cfd8; border-radius: 2px; }
.ldt-panel .scrollable::-webkit-scrollbar-button,
.ldt-panel .scrollable:hover::-webkit-scrollbar-button { display: none !important; }

/* Group */
.ldt-group {
  margin: 0;
  border-bottom: 1px solid #f0f3f7;
}
.ldt-group:last-child { border-bottom: none; }

.ldt-group-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0.75rem;
  cursor: pointer;
  transition: background 0.12s ease;
  user-select: none;
}
.ldt-group-row:hover {
  background: #f4f7fb;
}
.ldt-group-row--active {
  background: #e8f0f9;
}

.ldt-group-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1e293b;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.02em;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ldt-group-row--active .ldt-group-name {
  color: #063c6d;
}

.ldt-group-caret {
  font-size: 0.6rem;
  color: #94a3b8;
  flex-shrink: 0;
  margin-left: 0.375rem;
  transition: transform 0.18s ease, color 0.15s;
}
.ldt-group-caret--open {
  transform: rotate(90deg);
  color: #063c6d;
}
.ldt-group-row--active .ldt-group-caret { color: #063c6d; }

/* Active group left bar */
.ldt-group-row--active {
  border-left: 3px solid #063c6d;
  padding-left: 0.5625rem;
}
.ldt-group-row:not(.ldt-group-row--active) {
  border-left: 3px solid transparent;
}

/* Chips */
.ldt-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem 0.5rem 0.9375rem;
}

.ldt-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 0.5625rem;
  border-radius: 20px;
  font-size: 0.63rem;
  font-family: 'Courier New', monospace;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;
  outline: none;
  border: 1.5px solid #dde3ec;
  background: #f8fafc;
  color: #475569;
  transition: all 0.13s ease;
}
.ldt-chip:hover {
  border-color: #063c6d;
  background: #eaf1fb;
  color: #063c6d;
}
.ldt-chip--on {
  background: #063c6d;
  border-color: #063c6d;
  color: #fff;
  box-shadow: 0 1px 4px rgba(6,60,109,0.25);
}
.ldt-chip--on:hover {
  background: #0a4d8c;
  border-color: #0a4d8c;
  color: #fff;
}

/* Slide animation */
.ldt-slide-enter-active,
.ldt-slide-leave-active {
  transition: max-height 0.18s ease, opacity 0.15s ease;
  max-height: 18.75rem;
  overflow: hidden;
}
.ldt-slide-enter,
.ldt-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

/* State views */
.ldt-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 1rem;
  text-align: center;
}
.ldt-state-ico {
  font-size: 1.6rem;
  color: #063c6d;
}
.ldt-state-ico--blue { color: #063c6d; }
.ldt-state-ico--muted { color: #cbd5e1; }
.ldt-state-msg {
  font-size: 0.72rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}
.ldt-state-msg strong { color: #1e293b; }

/* Neutralise Bootstrap btn-primary inside panel */
.ldt-panel .btn-primary {
  background: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  color: inherit !important;
}
</style>

<script>
import {Tile as TileLayer} from 'ol/layer'
import {XYZ} from 'ol/source'
export default {
  name: "Generic_LidarTiles",
  props: [
    'showLidarPanel',
    'lidarMinZoomAchieved',
    'distinct_lidar',
    'lidarIsLoading',
    'map',
    'selectedExtent',
    'zoomLevel'
  ],
  data() {
    return {
      lidarPanelVisible: true,
      apiUrl: null,
      lidarGroup: null,
      currentIndex: 0,
      lidarMinimized: false
    }
  },
  methods: {
    toggleLidarHeight() {
      this.lidarMinimized = !this.lidarMinimized
    },
    expandLidarGroup(IDobj) {
      IDobj.groupIsCollapsed = !IDobj.groupIsCollapsed
    },
    toggleLidarLayer(pointcloudID, group, turnWholeGroupOn = false) {
      let needToCreateLidarLayer = true

      this.map.getLayers().forEach(layer => {
        if (layer.get('name') == `${pointcloudID}lidar`) {
          needToCreateLidarLayer = false
          if (turnWholeGroupOn) {
            layer.setVisible(true)
          } else if (layer.getVisible()) {
            layer.setVisible(false)
          } else {
            layer.setVisible(true)
          }
        }
      })

      if (needToCreateLidarLayer) {
        let url
        switch (this.extentName) {
          default:
            url = `https://drt-2d.ushrauto-dev.com/${pointcloudID}/{z}/{x}/{-y}_0.png`
            break
          case 'Japan':
            url = `https://2d-japan.ushrauto-dev.com/${pointcloudID}/{z}/{x}/{-y}_0.png`
            break
          case 'Europe':
            url = `https://drt-2d-europe.ushrauto-dev.com/${pointcloudID}/{z}/{x}/{-y}_0.png`
            break
          case 'Middle East':
            url = `https://2d-me.ushrauto-dev.com/${pointcloudID}/{z}/{x}/{-y}_0.png`
            break
          case 'Kuwait':
            url = `https://2d-me.ushrauto-dev.com/${pointcloudID}/{z}/{x}/{-y}_0.png`
            break
          case 'Saudi Arabia':
            url = `https://2d-me.ushrauto-dev.com/${pointcloudID}/{z}/{x}/{-y}_0.png`
            break
        }
        let newLidarLayer = new TileLayer({
          name: pointcloudID + 'lidar',
          visible: true,
          opacity: 1.0,
          source: new XYZ({ url }),
          zIndex: group
        })
        this.map.addLayer(newLidarLayer)
      }
      this.$emit('newLidarDateCode', group)
    },
    availableLidarButtonCtrl(pointcloudID) {
      let returnClass = 'lidar-off'
      this.map.getLayers().forEach(layer => {
        if (layer.get('name') == pointcloudID + 'lidar') {
          layer.getVisible()
            ? (returnClass = 'btn-primary')
            : (returnClass = 'lidar-off')
        }
      })
      return returnClass
    },
    toggleLidarGroup(IDobj, handleVisibility, event) {
      let group = IDobj.dateCode.toString()
      let groupHasLayerCurrentlyVisible = false

      this.map.getLayers().forEach(layer => {
        let layerNameStr = layer.get('name') ? layer.get('name').toString() : null
        if (layerNameStr && layerNameStr.includes(group) && layer.getVisible()) {
          groupHasLayerCurrentlyVisible = true
        }
      })

      if (groupHasLayerCurrentlyVisible) {
        if (handleVisibility) {
          this.map.getLayers().forEach(layer => {
            let layerNameStr = layer.get('name') ? layer.get('name').toString() : null
            if (layerNameStr && layerNameStr.includes(group)) {
              layer.setVisible(false)
            }
          })
        }
      } else {
        IDobj.pointcloudIDArr.forEach(pointcloudID => {
          this.toggleLidarLayer(pointcloudID, IDobj.dateCode, true)
        })
      }
      event.target.blur()
    },
    anyPointcloudOfLidarGroupVisible(dateCode) {
      let returnClass = 'lidar-group-off'
      let group = dateCode.toString()
      this.map.getLayers().forEach(layer => {
        let layerNameStr = layer.get('name') ? layer.get('name').toString() : null
        if (layerNameStr && layerNameStr.includes(group) && layer.getVisible()) {
          returnClass = 'btn-primary'
        }
      })
      return returnClass
    },
  },
  watch: {
    zoomLevel(newValue) {
      this.lidarPanelVisible = newValue >= 17
    },
  }
}
</script>
